import styled from 'styled-components';
import { motion } from 'framer-motion';
import fonts from '../../design-system/fonts';
import colors from '../../design-system/colors';

export const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${colors.porcelain};
`;

export const Container = styled(motion.div)`
  flex: 1;
  padding: 120px 40px 80px;
  margin: 0 auto;
  width: 100%;
  max-width: 1400px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 100px 30px 60px;
  }

  @media (max-width: 768px) {
    padding: 100px 20px 60px;
  }

  @media (max-width: 480px) {
    padding: 90px 16px 40px;
  }
`;

export const Section = styled(motion.div)`
  margin-bottom: 60px;
`;

export const Title = styled.h1`
  font-size: 72px;
  font-weight: 900;
  font-style: normal;
  color: ${colors.sapphire};
  text-transform: none;
  margin: 0;
  font-family: ${fonts.headline};
  word-wrap: break-word;
  letter-spacing: -0.03em;
  line-height: 1.1;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 0;
    width: 100px;
    height: 4px;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 48px;
  }

  @media (max-width: 480px) {
    font-size: 36px;
  }
`;

export const ContactInfo = styled(motion.p)`
  font-size: 18px;
  line-height: 1.6;
  color: ${colors.deepCharcoal};
  opacity: 0.6;
  margin: 40px 0 0 0;
  font-family: ${fonts.body};

  @media (max-width: 768px) {
    font-size: 16px;
    margin: 30px 0 0 0;
  }
`;

export const TwoColumnLayout = styled.div`
  display: flex;
  gap: 60px;
  align-items: flex-start;
  margin-top: 60px;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 40px;
    margin-top: 50px;
  }

  @media (max-width: 768px) {
    gap: 30px;
    flex-direction: column;
    margin-top: 40px;
  }

  @media (max-width: 480px) {
    gap: 24px;
    margin-top: 30px;
  }
`;

export const FormColumn = styled.div`
  flex: 1.2;
  min-width: 0;
  width: 100%;
  max-width: 100%;

  @media (max-width: 1024px) {
    flex: 1;
    width: 100%;
  }
`;

export const InfoColumn = styled.div`
  flex: 0.8;
  display: flex;
  flex-direction: column;
  gap: 40px;
  min-width: 0;

  @media (max-width: 1024px) {
    flex: 1;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px 40px;
    align-items: start;
  }

  @media (max-width: 768px) {
    gap: 30px;
  }

  @media (max-width: 480px) {
    gap: 24px;
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 20px;
    width: 100%;
  }

  @media (max-width: 480px) {
    gap: 16px;
    width: 100%;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    gap: 6px;
  }
`;

export const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.deepCharcoal};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: ${fonts.body};

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

export const Input = styled.input`
  padding: 14px 16px;
  border: 1px solid ${colors.porcelain};
  border-radius: 4px;
  font-size: 16px;
  color: ${colors.deepCharcoal};
  font-family: ${fonts.body};
  transition: border-color 0.3s ease;
  background: #ffffff;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;

  &:focus {
    outline: none;
    border-color: ${colors.sapphire};
  }

  &::placeholder {
    color: ${colors.deepCharcoal};
    opacity: 0.4;
  }

  @media (max-width: 768px) {
    padding: 12px 14px;
    font-size: 16px; /* Prevent zoom on iOS */
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 16px; /* Keep 16px to prevent iOS zoom */
    width: 100%;
    max-width: 100%;
  }
`;

export const TextArea = styled.textarea`
  padding: 14px 16px;
  border: 1px solid ${colors.porcelain};
  border-radius: 4px;
  font-size: 16px;
  color: ${colors.deepCharcoal};
  font-family: ${fonts.body};
  transition: border-color 0.3s ease;
  resize: vertical;
  background: #ffffff;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;

  &:focus {
    outline: none;
    border-color: ${colors.sapphire};
  }

  &::placeholder {
    color: ${colors.deepCharcoal};
    opacity: 0.4;
  }

  @media (max-width: 768px) {
    padding: 12px 14px;
    font-size: 16px; /* Prevent zoom on iOS */
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 16px; /* Keep 16px to prevent iOS zoom */
    width: 100%;
    max-width: 100%;
  }
`;

export const SubmitButton = styled.button`
  padding: 16px 32px;
  background: ${colors.deepCharcoal};
  color: ${colors.porcelain};
  border: none;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${fonts.body};
  align-self: flex-start;
  margin-top: 8px;
  width: auto;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;

  &:hover {
    background: ${colors.sapphire};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 13px;
    align-self: stretch;
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 12px 24px;
    font-size: 12px;
    width: 100%;
    max-width: 100%;
  }
`;

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const InfoTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.deepCharcoal};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
  font-family: ${fonts.headline};

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

export const InfoText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: ${colors.deepCharcoal};
  opacity: 0.6;
  margin: 0;
  font-family: ${fonts.body};

  @media (max-width: 768px) {
    font-size: 15px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

export const InfoLink = styled.a`
  font-size: 16px;
  line-height: 1.6;
  color: ${colors.deepCharcoal};
  opacity: 0.6;
  text-decoration: none;
  transition: all 0.3s ease;
  font-family: ${fonts.body};
  word-break: break-word;

  &:hover {
    color: ${colors.sapphire};
    opacity: 1;
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    font-size: 15px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

export const StatusMessage = styled.div`
  padding: 16px;
  border-radius: 4px;
  font-size: 14px;
  font-family: ${fonts.body};
  margin-top: 8px;
  word-wrap: break-word;
  ${(props) =>
    props.success
      ? `
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `
      : props.error
      ? `
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `
      : ''}

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    font-size: 12px;
  }
`;

export const SubmitButtonDisabled = styled(SubmitButton)`
  opacity: 0.6;
  cursor: not-allowed;

  &:hover {
    background: ${colors.deepCharcoal};
  }
`;
